export const actionPlanData = {
  // Note from spreadsheet: "Tiers are cumulative: each level includes all actions from the levels below it."

  // CORE ACTIONS (original rubric) - shown to everyone
  coreActions: {
    label: "Core Actions (Original Rubric)",
    description: "Foundational steps from the TravelAbility framework",
    actions: [
      "Send stakeholder survey",
      "Convene lunch with local disability organizations",
      "Adopt the Accessibility Playbook for DMOs"
    ]
  },

  // ADDITIONAL ACTIONS - shown to everyone
  additionalActions: {
    label: "Additional Actions",
    description: "Quick wins to build momentum",
    actions: [
      "Distribute the We Don't Bite video (ODR Disability Sensitivity Training)",
      "Designate an internal accessibility champion"
    ]
  },

  // BUDGET-TIERED ACTIONS
  budgetTiers: {
    "under5k": {
      label: "Under $5K",
      groups: [
        {
          category: "Budget Actions",
          actions: [
            "Run a free website accessibility scan (e.g., WAVE) and fix quick wins like alt text, captions, and color contrast",
            "Add an \"Accessible [Destination]\" page to the website",
            "Add accessibility questions to partner/member onboarding",
            "Enroll staff in free online disability-etiquette training",
            "Use inclusive imagery in social media and the visitor guide",
            "Plug and Play catalog"
          ]
        },
        {
          category: "Accessible Visitor / Airport Information Center",
          actions: [
            "Stock sensory kits and noise-reducing headphones at the visitor center",
            "Add an assistive listening device and a lowered service counter",
            "Pay local residents with disabilities to review web and print content",
            "Create a downtown accessibility map (parking, restrooms, curb cuts, quiet spaces)",
            "Host a disability travel creator for a sponsored visit"
          ]
        }
      ]
    },
    "5k-15k": {
      label: "$5K – $15K",
      note: "Includes all actions from Under $5K",
      groups: []
    },
    "15k-30k": {
      label: "$15K – $30K",
      note: "Includes all actions from $5K–$15K",
      groups: [
        {
          category: "Expanded Actions",
          actions: [
            "Wheel the World assessments for priority partners",
            "Commission a professional website audit and remediation",
            "Launch a small partner micro-grant program for low-cost upgrades (ramps, grab bars, visual alarms)",
            "Run an inclusive photo/video shoot featuring people with disabilities",
            "Hold in-person frontline hospitality workshops",
            "Produce an accessible meetings and events guide for the sales team"
          ]
        }
      ]
    },
    "30k-50k": {
      label: "$30K – $50K",
      note: "Includes all actions from $15K–$30K",
      groups: [
        {
          category: "Advanced Initiatives",
          actions: [
            "KultureCity/Visitable training",
            "Expanded Wheel the World assessments",
            "Pursue sensory-inclusive certification for the DMO and key venues",
            "Form a paid disability advisory committee with member stipends",
            "Launch an adaptive equipment loan program (beach wheelchairs, all-terrain chairs)",
            "Map accessible transportation options with transit partners",
            "Run a targeted marketing campaign to disability travel audiences",
            "Set baseline accessibility KPIs"
          ]
        }
      ]
    },
    "over50k": {
      label: "$50K+",
      note: "Includes all actions from $30K–$50K",
      groups: [
        {
          category: "Strategic Core (Long-Term Transformation)",
          actions: [
            "Join DAC",
            "Create an accessibility strategic plan",
            "Hire or dedicate an accessibility manager",
            "Establish a multi-year partner grant fund",
            "Set destination-wide certification goals and publish an annual accessibility progress report",
            "Partner with the city on capital projects (accessible trails, adaptive recreation, beach access)",
            "Sponsor or bid on disability-focused events and conferences",
            "Engage an accessibility consultant to guide long-term implementation"
          ]
        }
      ]
    }
  },

  tierOrder: ["under5k", "5k-15k", "15k-30k", "30k-50k", "over50k"]
};

/**
 * Get the full action plan based on budget tier
 * Cumulative: includes all tiers at and below the selected level
 */
export const getActionPlan = (budgetTierId) => {
  const plan = {
    coreActions: actionPlanData.coreActions,
    additionalActions: actionPlanData.additionalActions,
    budgetGroups: []
  };

  if (!budgetTierId) return plan;

  const tierIndex = actionPlanData.tierOrder.indexOf(budgetTierId);
  if (tierIndex === -1) return plan;

  // Collect all groups from selected tier and below (cumulative)
  for (let i = 0; i <= tierIndex; i++) {
    const tierId = actionPlanData.tierOrder[i];
    const tier = actionPlanData.budgetTiers[tierId];
    if (tier && tier.groups.length > 0) {
      plan.budgetGroups.push({
        tierLabel: tier.label,
        note: tier.note || null,
        groups: tier.groups
      });
    }
  }

  return plan;
};
